import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import { api } from '../../api';
import { useAuth, warmupBackend, LOGIN_TIMEOUT_MS } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../utils/apiErrorMessages';

/**
 * One-click demo sign-in: URL is only ?magic=… (tenant/user come from the token row).
 */
const DemoMagicSignIn = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const auth = useAuth();
    const [error, setError] = useState('');
    const startedRef = useRef(false);

    useEffect(() => {
        const magic = new URLSearchParams(location.search).get('magic')?.trim();
        if (!magic) {
            navigate('/', { replace: true });
            return undefined;
        }
        if (startedRef.current) return undefined;
        startedRef.current = true;

        auth.logout();

        const ac = new AbortController();
        (async () => {
            try {
                await warmupBackend();
                const { data } = await api.post(
                    '/auth/demo-magic-consume',
                    { magic },
                    { timeout: LOGIN_TIMEOUT_MS, signal: ac.signal },
                );
                const slug = (data.tenant_slug || 'ciademo').trim().toLowerCase();
                await auth.completeLoginWithToken(data.access_token, slug, {
                    mustChangePassword: false,
                });
                navigate('/app', { replace: true });
            } catch (err) {
                if (ac.signal.aborted) return;
                setError(
                    getApiErrorMessage(
                        err,
                        'This sign-in link is invalid, expired, or was already used.',
                    ),
                );
            }
        })();
        return () => ac.abort();
    }, [location.search, auth, navigate]);

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--archon-navy-deep)',
                padding: 24,
            }}
        >
            <div style={{ maxWidth: 420, width: '100%', textAlign: 'center', color: '#d0d5dd' }}>
                {!error ? (
                    <>
                        <Spinner animation="border" variant="primary" className="mb-3" />
                        <p className="mb-0">Signing you in to the demo…</p>
                    </>
                ) : (
                    <Alert variant="danger" className="text-start">
                        {error}
                        <div className="mt-3 small text-muted">
                            Use the manual sign-in link in your email (company ID, username, and
                            temporary password) or ask your administrator for a new invitation.
                        </div>
                    </Alert>
                )}
            </div>
        </div>
    );
};

export default DemoMagicSignIn;
